import { useCallback } from "react";

type Theme = {
  bg: string;
  surface: string;
  accent: string;
  accentText: string;
  text: string;
  textMuted: string;
  textFaint: string;
  border: string;
  inputBg: string;
  font: string;
  ornament: string;
};

type InvitationData = {
  event_title: string | null;
  title: string;
  event_date: string | null;
  event_time: string | null;
  ceremony_name: string | null;
  venue_name: string | null;
  ceremony_address: string | null;
  venue_address: string | null;
  ceremony_maps_url: string | null;
  google_maps_url: string | null;
  reception_name: string | null;
  reception_address: string | null;
  reception_maps_url: string | null;
  thank_you_message: string | null;
};

interface ThankYouScreenProps {
  invitation: InvitationData;
  theme: Theme;
  onAddGuest: () => void;
}

export default function ThankYouScreen({ invitation, theme, onAddGuest }: ThankYouScreenProps) {
  const formattedDate = invitation.event_date
    ? new Date(invitation.event_date).toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const text = `${invitation.event_title || invitation.title} — ¡Únete a la celebración!`;
    if (navigator.share) {
      try { await navigator.share({ title: invitation.event_title || invitation.title, text, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url).catch(() => {});
    }
  }, [invitation]);

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${invitation.event_title || invitation.title} — ${window.location.href}`
  )}`;

  return (
    <div className="text-center space-y-6 animate-fadeIn">
      {/* Icon */}
      <div
        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center text-3xl"
        style={{ backgroundColor: theme.accent + "18", color: theme.accent }}
      >
        <i className="ri-heart-3-fill" />
      </div>

      {/* Message */}
      <div className="space-y-2">
        <p className="font-bold text-2xl leading-tight" style={{ color: theme.text, fontFamily: theme.font }}>
          {invitation.thank_you_message || "¡Gracias por confirmar!"}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
          Te esperamos con mucho cariño. Aquí tienes un resumen del evento.
        </p>
      </div>

      {/* Event summary card */}
      <div className="rounded-2xl border text-left overflow-hidden" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
        <div className="px-5 py-5 space-y-3">
          {formattedDate && (
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-0.5"
                style={{ backgroundColor: theme.accent + "12", color: theme.accent }}>
                <i className="ri-calendar-event-line text-sm" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>Fecha</p>
                <p className="text-sm font-medium capitalize" style={{ color: theme.text }}>{formattedDate}</p>
                {invitation.event_time && (
                  <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>{invitation.event_time}</p>
                )}
              </div>
            </div>
          )}

          {(invitation.ceremony_name || invitation.venue_name) && (
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-0.5"
                style={{ backgroundColor: theme.accent + "12", color: theme.accent }}>
                <i className="ri-map-pin-2-line text-sm" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>Ceremonia</p>
                <p className="text-sm font-medium" style={{ color: theme.text }}>
                  {invitation.ceremony_name || invitation.venue_name}
                </p>
                {(invitation.ceremony_address || invitation.venue_address) && (
                  <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                    {invitation.ceremony_address || invitation.venue_address}
                  </p>
                )}
              </div>
            </div>
          )}

          {invitation.reception_name && (
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-0.5"
                style={{ backgroundColor: theme.accent + "12", color: theme.accent }}>
                <i className="ri-map-pin-3-line text-sm" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>Recepción</p>
                <p className="text-sm font-medium" style={{ color: theme.text }}>{invitation.reception_name}</p>
                {invitation.reception_address && (
                  <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>{invitation.reception_address}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location buttons */}
      <div className="space-y-2.5">
        {(invitation.ceremony_maps_url || invitation.google_maps_url) && (
          <a
            href={invitation.ceremony_maps_url || invitation.google_maps_url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl font-semibold text-sm cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: theme.accent, color: theme.accentText }}
          >
            <i className="ri-navigation-line" />
            Cómo llegar
          </a>
        )}
        {invitation.reception_maps_url && (
          <a
            href={invitation.reception_maps_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl font-semibold text-sm cursor-pointer whitespace-nowrap border"
            style={{ backgroundColor: "transparent", color: theme.accent, borderColor: theme.border }}
          >
            <i className="ri-map-pin-3-line" />
            Ubicación recepción
          </a>
        )}
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px" style={{ backgroundColor: theme.border }} />
        <span className="text-xs" style={{ color: theme.textFaint }}>Invita a más personas</span>
        <div className="flex-1 h-px" style={{ backgroundColor: theme.border }} />
      </div>

      {/* Add more guests + share */}
      <div className="space-y-2.5">
        <button
          onClick={onAddGuest}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-medium text-sm cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: theme.accent + "12", color: theme.accent }}
        >
          <i className="ri-user-add-line" />
          Agregar más invitados
        </button>

        <button
          onClick={handleShare}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-medium text-sm cursor-pointer whitespace-nowrap border"
          style={{ backgroundColor: "transparent", color: theme.textMuted, borderColor: theme.border }}
        >
          <i className="ri-share-line" />
          Compartir invitación
        </button>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-medium text-sm cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: "#22c55e18", color: "#16a34a" }}
        >
          <i className="ri-whatsapp-line" />
          Compartir por WhatsApp
        </a>
      </div>
    </div>
  );
}